<script>
import Layout from "../../layouts/main";
import PageHeader from "@/components/page-header";
import i18n from "@/i18n";
import { mapState } from "vuex";
import { LOOKUP_MODULE } from "@/state/modules/lookup";
import { storeMethods, lookupMethods } from "@/state/helpers";
import Multiselect from "vue-multiselect";
import "vue-multiselect/dist/vue-multiselect.min.css";

export default {
  page: { title: i18n.t("label.listingX", [i18n.t("module.store")]) },
  components: { Layout, PageHeader, Multiselect },
  data() {
    return {
      title: i18n.t("label.listingX", [i18n.t("module.store")]),
      breadcrumbItems: [
        { text: i18n.t("module.customer"), href: "/" },
        { text: i18n.t("module.store"), active: true },
      ],
      search: "",
      selectedType: null,
      selectedDebtor: null,
      fields: [
        { key: "name", label: i18n.t("label.name") },
        { key: "storeType", label: i18n.t("store.type.label") },
        { key: "city", label: i18n.t("label.city") },
        { key: "state", label: i18n.t("label.state") },
        { key: "phone", label: i18n.t("label.phone") },
        { key: "isActive", label: i18n.t("label.status") },
        { key: "action", label: i18n.t("label.action") },
      ],
    };
  },
  computed: {
    ...mapState(LOOKUP_MODULE, ["debtorDdl"]),
    tableRows() { return this.$store.state.store.stores.data; },
    typeOptions() {
      return [
        { value: "direct", text: i18n.t("store.type.direct") },
        { value: "dealer", text: i18n.t("store.type.dealer") },
      ];
    },
  },
  async created() {
    await Promise.all([this.reload(), this.lookupDebtor({})]);
  },
  methods: {
    ...storeMethods,
    ...lookupMethods,
    buildFilter() {
      const f = {};
      if (this.search) f.search = this.search;
      if (this.selectedType) f.store_type = this.selectedType.value;
      if (this.selectedDebtor) f.debtor_code = this.selectedDebtor.account_no;
      return f;
    },
    reload() {
      return this.getAllStores(this.buildFilter());
    },
    onTypeChange(t) {
      this.selectedType = t || null;
      this.reload();
    },
    onDebtorChange(d) {
      this.selectedDebtor = d || null;
      this.reload();
    },
    goCreate() {
      this.$router.push({ name: "Store-create" });
    },
    goEdit(row) {
      this.$router.push({ name: "Store-update", params: { uuid: row.uuid } });
    },
    openMap(row) {
      if (row.latitude == null || row.longitude == null) return;
      window.open(
        `https://www.google.com/maps?q=${row.latitude},${row.longitude}`,
        "_blank"
      );
    },
    async remove(row) {
      const ok = await this.$bvModal.msgBoxConfirm(
        this.$t("label.deleteX", [this.$t("module.store")]) + "?",
        {
          okTitle: this.$t("label.delete"),
          cancelTitle: this.$t("label.cancel"),
          okVariant: "danger",
          centered: true,
        }
      );
      if (!ok) return;
      await this.deleteStore({ uuid: row.uuid });
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="breadcrumbItems" />
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <b-row class="mb-3 align-items-end">
              <b-col cols="12" md="4">
                <label class="d-block mb-1 small text-muted">{{ $t('label.search') }}</label>
                <b-form-input
                  v-model="search"
                  type="search"
                  size="sm"
                  :placeholder="$t('label.name') + ' / ' + $t('label.city') + ' / ' + $t('label.state')"
                  @change="reload"
                />
              </b-col>
              <b-col cols="12" md="4">
                <label class="d-block mb-1 small text-muted">{{ $t('store.type.label') }}</label>
                <multiselect
                  :value="selectedType"
                  :options="typeOptions"
                  track-by="value"
                  label="text"
                  open-direction="below"
                  :show-labels="false"
                  :placeholder="$t('label.select')"
                  @input="onTypeChange"
                />
              </b-col>
              <b-col cols="12" md="4">
                <label class="d-block mb-1 small text-muted">{{ $t('store.debtor') }}</label>
                <multiselect
                  :value="selectedDebtor"
                  :options="debtorDdl"
                  track-by="account_no"
                  :custom-label="(d) => `${d.account_no} — ${d.company_name || ''}`"
                  open-direction="below"
                  :show-labels="false"
                  :placeholder="$t('label.select')"
                  @input="onDebtorChange"
                />
              </b-col>
            </b-row>
            <div class="text-right mb-3">
              <b-button v-if="$can('Create Store')" variant="primary" size="sm" @click="goCreate">
                <i class="mdi mdi-plus mr-1"></i>{{ $t('label.createX', [$t('module.store')]) }}
              </b-button>
            </div>
            <b-table :items="tableRows" :fields="fields" responsive="sm" small show-empty>
              <template #cell(storeType)="data">
                <b-badge :variant="tableRows[data.index].storeType === 'dealer' ? 'info' : 'primary'">
                  {{ tableRows[data.index].storeType === 'dealer' ? $t('store.type.dealer') : $t('store.type.direct') }}
                </b-badge>
                <small v-if="tableRows[data.index].storeType === 'dealer' && tableRows[data.index].debtorCode" class="text-muted ml-1">
                  {{ tableRows[data.index].debtorCode }}
                </small>
              </template>
              <template #cell(isActive)="data">
                <b-badge :variant="tableRows[data.index].isActive ? 'success' : 'secondary'">
                  {{ tableRows[data.index].isActive ? $t('label.active') : $t('label.inactive') }}
                </b-badge>
              </template>
              <template #cell(action)="data">
                <a v-if="tableRows[data.index].latitude != null && tableRows[data.index].longitude != null" href="javascript:void(0);" @click="openMap(tableRows[data.index])" class="text-success mr-2" v-b-tooltip.hover :title="$t('store.openMap')">
                  <i class="mdi mdi-map-marker font-size-18"></i>
                </a>
                <a v-if="$can('Update Store')" href="javascript:void(0);" @click="goEdit(tableRows[data.index])" class="text-info mr-2" v-b-tooltip.hover :title="$t('label.updateX', [$t('module.store')])">
                  <i class="mdi mdi-pencil font-size-18"></i>
                </a>
                <a v-if="$can('Delete Store')" href="javascript:void(0);" @click="remove(tableRows[data.index])" class="text-danger" v-b-tooltip.hover :title="$t('label.delete')">
                  <i class="mdi mdi-delete font-size-18"></i>
                </a>
              </template>
            </b-table>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>
